import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressRestaurantsOrdersV3OrdersApiUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {};

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_restaurants_orders';

/**
 * TEST The `getOrder()` function returns a Promise that is returned when an order is retrieved.
 *
 * 1. First do this.
 * 1. The do that
 *     + This is a bullet
 *     + So is this.
 *
 * > **Note**: This indentation is not how our notes generally look but I will waiit to see the branch preview. I am just trying out some markdown here.
 * @deprecated It has been replaced with com.wix.ecom.orders.v1.Orders.GetOrder(), and will be removed on 2025-09-30.
 */
export function getOrder(payload: object): RequestOptionsFactory<any> {
  function __getOrder({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v3.order',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.restaurants.orders.v3.OrdersApi.GetOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsOrdersV3OrdersApiUrl({
        protoPath: '/v3/orders/{id}',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.fulfillment.promisedTime' },
              {
                path: 'order.fulfillment.deliveryDetails.externalProvider.pickupTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.externalProvider.dropOffTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.pickupInfo.windowStartTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.pickupInfo.windowEndTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.dropOffInfo.windowStartTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.dropOffInfo.windowEndTime',
              },
              { path: 'order.activities.timestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'order.fulfillment.deliveryDetails.address.location.latitude',
              },
              {
                path: 'order.fulfillment.deliveryDetails.address.location.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getOrder;
}

/**
 * Retrieves a list of up to 1000 orders, given the provided filters.
 * @deprecated It has been replaced with com.wix.ecom.orders.v1.Orders.SearchOrders(), and will be removed on 2025-09-30.
 */
export function listOrders(payload: object): RequestOptionsFactory<any> {
  function __listOrders({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v3.order',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.restaurants.orders.v3.OrdersApi.ListOrders',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsOrdersV3OrdersApiUrl({
        protoPath: '/v3/orders',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'orders.createdDate' },
              { path: 'orders.updatedDate' },
              { path: 'orders.fulfillment.promisedTime' },
              {
                path: 'orders.fulfillment.deliveryDetails.externalProvider.pickupTime',
              },
              {
                path: 'orders.fulfillment.deliveryDetails.externalProvider.dropOffTime',
              },
              {
                path: 'orders.fulfillment.deliveryDetails.pickupInfo.windowStartTime',
              },
              {
                path: 'orders.fulfillment.deliveryDetails.pickupInfo.windowEndTime',
              },
              {
                path: 'orders.fulfillment.deliveryDetails.dropOffInfo.windowStartTime',
              },
              {
                path: 'orders.fulfillment.deliveryDetails.dropOffInfo.windowEndTime',
              },
              { path: 'orders.activities.timestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'orders.fulfillment.deliveryDetails.address.location.latitude',
              },
              {
                path: 'orders.fulfillment.deliveryDetails.address.location.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listOrders;
}

/**
 * Changes the order status to `ACCEPTED`.
 *
 * > **Note:** It is not possible to change the order status from `FULFILLED` to `ACCEPTED`.
 * @deprecated Will be removed on 2025-09-30.
 */
export function acceptOrder(payload: object): RequestOptionsFactory<any> {
  function __acceptOrder({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'pickupTime' }, { path: 'dropOffTime' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v3.order',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.restaurants.orders.v3.OrdersApi.AcceptOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsOrdersV3OrdersApiUrl({
        protoPath: '/v3/orders/{id}/accept',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.fulfillment.promisedTime' },
              {
                path: 'order.fulfillment.deliveryDetails.externalProvider.pickupTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.externalProvider.dropOffTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.pickupInfo.windowStartTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.pickupInfo.windowEndTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.dropOffInfo.windowStartTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.dropOffInfo.windowEndTime',
              },
              { path: 'order.activities.timestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'order.fulfillment.deliveryDetails.address.location.latitude',
              },
              {
                path: 'order.fulfillment.deliveryDetails.address.location.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __acceptOrder;
}

/**
 * Changes the order status to `CANCELED`.
 * @deprecated It has been replaced with com.wix.ecom.orders.v1.Orders.CancelOrder(), and will be removed on 2025-09-30.
 */
export function cancelOrder(payload: object): RequestOptionsFactory<any> {
  function __cancelOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v3.order',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.restaurants.orders.v3.OrdersApi.CancelOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsOrdersV3OrdersApiUrl({
        protoPath: '/v3/orders/{id}/cancel',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.fulfillment.promisedTime' },
              {
                path: 'order.fulfillment.deliveryDetails.externalProvider.pickupTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.externalProvider.dropOffTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.pickupInfo.windowStartTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.pickupInfo.windowEndTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.dropOffInfo.windowStartTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.dropOffInfo.windowEndTime',
              },
              { path: 'order.activities.timestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'order.fulfillment.deliveryDetails.address.location.latitude',
              },
              {
                path: 'order.fulfillment.deliveryDetails.address.location.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelOrder;
}

/**
 * Changes the order status to `FULFILLED`.
 * @deprecated Will be removed on 2025-09-30.
 */
export function fulfillOrder(payload: object): RequestOptionsFactory<any> {
  function __fulfillOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v3.order',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.restaurants.orders.v3.OrdersApi.FulfillOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsOrdersV3OrdersApiUrl({
        protoPath: '/v3/orders/{id}/fulfill',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'order.createdDate' },
              { path: 'order.updatedDate' },
              { path: 'order.fulfillment.promisedTime' },
              {
                path: 'order.fulfillment.deliveryDetails.externalProvider.pickupTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.externalProvider.dropOffTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.pickupInfo.windowStartTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.pickupInfo.windowEndTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.dropOffInfo.windowStartTime',
              },
              {
                path: 'order.fulfillment.deliveryDetails.dropOffInfo.windowEndTime',
              },
              { path: 'order.activities.timestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'order.fulfillment.deliveryDetails.address.location.latitude',
              },
              {
                path: 'order.fulfillment.deliveryDetails.address.location.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __fulfillOrder;
}
